/**
 * Resolve the API base URL.
 *  - Explicit env var wins (VITE_API_URL)
 *  - Inside Tauri desktop: use the production gateway directly
 *  - Web deployment: nginx proxies /api/ → gateway
 */
function resolveApiBase(): string {
  const env = import.meta.env.VITE_API_URL as string | undefined;
  if (env) return env;
  if (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window) {
    return 'https://app.eurion.se/api';
  }
  return '/api';
}

export const API_BASE = resolveApiBase();

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
  requestId?: string;
}

/** Seconds until a JWT expires (0 if unreadable or already expired). */
export function secondsLeft(jwt: string, now = Date.now()): number {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof payload.exp === 'number' ? Math.max(0, payload.exp - now / 1000) : 0;
  } catch {
    return 0;
  }
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private refreshTokenStr: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('eurion_token');
    this.refreshTokenStr = localStorage.getItem('eurion_refresh_token');
  }

  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('eurion_token', token);
    } else {
      localStorage.removeItem('eurion_token');
      this.setRefreshToken(null);
    }
  }

  setRefreshToken(token: string | null): void {
    this.refreshTokenStr = token;
    if (token) {
      localStorage.setItem('eurion_refresh_token', token);
    } else {
      localStorage.removeItem('eurion_refresh_token');
    }
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    const requestHeaders: Record<string, string> = {
      ...headers,
    };

    // Only set Content-Type when there's actually a body to send.
    // Sending Content-Type: application/json with an empty body causes
    // Fastify to reject the request with FST_ERR_CTP_EMPTY_JSON_BODY.
    if (body !== undefined && body !== null) {
      requestHeaders['Content-Type'] = 'application/json';
    }

    if (this.token) {
      requestHeaders['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: requestHeaders,
      body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (response.status === 401) {
      // Auto-refresh token
      const refreshed = await this.refreshToken();
      if (refreshed) {
        return this.request<T>(method, path, body, headers);
      }
      this.setToken(null);
      window.location.href = `${import.meta.env.BASE_URL}login`;   // /admin/login inside the admin portal
    }

    return response.json() as Promise<ApiResponse<T>>;
  }

  /**
   * An access token valid for at least another minute, refreshing it first if needed.
   * For WebSocket (re)connects, which can't recover from a 401 the way request() does:
   * reconnecting with an expired token was refused until something else refreshed it.
   */
  async freshToken(): Promise<string | null> {
    if (this.token && secondsLeft(this.token) > 60) return this.token;
    await this.refreshToken();
    return this.token;
  }

  private refreshing: Promise<boolean> | null = null;

  /** One refresh at a time: parallel 401s share it (a reused refresh token would be refused). */
  private refreshToken(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => { this.refreshing = null; });
    return this.refreshing;
  }

  /**
   * A refresh token works once (identity-service rotates it), and every tab keeps its own copy.
   * Tabs take turns (Web Locks) and each first adopts a token another tab already refreshed
   * into localStorage, so one tab's refresh doesn't sign the others out.
   */
  private async doRefresh(): Promise<boolean> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    return locks ? locks.request('eurion-token-refresh', () => this.refreshOnce()) : this.refreshOnce();
  }

  /** Take over tokens another tab stored since we last looked; true if they are usable. */
  private adoptStoredTokens(): boolean {
    const storedRefresh = localStorage.getItem('eurion_refresh_token');
    if (!storedRefresh || storedRefresh === this.refreshTokenStr) return false;
    this.refreshTokenStr = storedRefresh;
    const storedAccess = localStorage.getItem('eurion_token');
    if (storedAccess && secondsLeft(storedAccess) > 30) {
      this.token = storedAccess;
      return true;
    }
    return false;
  }

  private async refreshOnce(): Promise<boolean> {
    if (this.adoptStoredTokens()) return true;
    if (!this.refreshTokenStr) return false;
    try {
      const response = await fetch(`${this.baseUrl}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshTokenStr }),
        credentials: 'include',
      });
      if (response.ok) {
        const data = (await response.json()) as ApiResponse<{ accessToken: string; refreshToken?: string }>;
        if (data.data?.accessToken) {
          this.setToken(data.data.accessToken);
          if (data.data.refreshToken) {
            this.setRefreshToken(data.data.refreshToken);
          }
          return true;
        }
      }
      // Refused: another tab (without Web Locks) may have used the same token a moment ago
      return this.adoptStoredTokens();
    } catch {
      return false;
    }
  }

  get<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, body);
  }

  patch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, body);
  }

  put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, body);
  }

  delete<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path);
  }

  async upload(path: string, file: File, metadata?: Record<string, string>): Promise<ApiResponse<unknown>> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    if (metadata) {
      for (const [key, value] of Object.entries(metadata)) {
        headers[`x-${key}`] = value;
      }
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    return response.json() as Promise<ApiResponse<unknown>>;
  }
}

export const api = new ApiClient(API_BASE);
